import React, { useEffect, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import CostResults from "../costResults/CostResults";
import "./CostStep.css";
import {
  MOVABLE_RATIO,
  UNIT_ROWS,
  REPORT_TYPES,
  RESOURCE_CATEGORIES,
  newResource,
} from "../../../conts/simulationConstants";
import ReportTypePicker from "../../atoms/reportTypePicker/ReportTypePicker";

const num = (v) => (v === "" || v == null ? 0 : Number(v));
// a resource row is either fully empty (ignored) or fully filled
const RESOURCE_FIELDS = ["label", "quantity", "unitCost"];
const isBlank = (v) => v == null || String(v).trim() === "";
const getMissingFields = (r) => {
  const missing = RESOURCE_FIELDS.filter((f) => isBlank(r[f]));
  return missing.length === RESOURCE_FIELDS.length ? [] : missing;
};
const isCompleteRow = (r) => RESOURCE_FIELDS.every((f) => !isBlank(r[f]));
const CostStep = ({
  onBack,
  onNext,
  onLaunchCost,
  cost,
  canContinue = true,
  unitValues,
  setUnitValues,
  resources,
  setResources,
  reportType,
  setReportType,
}) => {
  const [inputsOpen, setInputsOpen] = useState(!cost?.data);
  // errors are shown only after a failed "Calculer", then update live
  const [showErrors, setShowErrors] = useState(false);
  const inputsRef = useRef(null);

  const missingById = showErrors
    ? Object.fromEntries(
        resources
          .map((r) => [r.id, getMissingFields(r)])
          .filter(([, missing]) => missing.length > 0),
      )
    : {};
  const incompleteCount = Object.keys(missingById).length;
  const isMissing = (id, field) => Boolean(missingById[id]?.includes(field));
  const invalidClass = (id, field) =>
    isMissing(id, field) ? "is-invalid" : "";
  // fold the form automatically when cost results arrive
  // (adjust `cost?.data` if your cost object exposes results under another key)
  useEffect(() => {
    if (cost?.data) setInputsOpen(false);
  }, [cost?.data]);

  const handleUnitChange = (key, value) =>
    setUnitValues((prev) => ({ ...prev, [key]: value }));

  const handleAddResource = (category) =>
    setResources((prev) => [...prev, newResource(category)]);

  const handleRemoveResource = (id) =>
    setResources((prev) => prev.filter((r) => r.id !== id));

  const handleResourceChange = (id, field, value) =>
    setResources((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );

  // derived value, display only (the backend doesn't expect it)
  const movableValue =
    unitValues.building === undefined || unitValues.building === ""
      ? ""
      : +(num(unitValues.building) * MOVABLE_RATIO).toFixed(2);

  // recap shown when the form is folded
  const filledUnits = UNIT_ROWS.filter(
    ({ key, derived }) =>
      !derived && unitValues[key] !== undefined && unitValues[key] !== "",
  ).length;
  const namedResources = resources.filter((r) => r.label.trim());
  const resourcesTotal = namedResources.reduce(
    (sum, r) => sum + num(r.quantity) * num(r.unitCost),
    0,
  );

  const handleCalculate = () => {
    const hasIncomplete = resources.some((r) => getMissingFields(r).length > 0);
    if (hasIncomplete) {
      setShowErrors(true);
      // bring the first empty field into view
      requestAnimationFrame(() =>
        inputsRef.current?.querySelector('[aria-invalid="true"]')?.focus(),
      );
      return;
    }
    setShowErrors(false);
    const payload = {
      ressources: resources.filter(isCompleteRow).map((r) => ({
        type: r.category,
        name: r.label.trim(),
        units: num(r.quantity),
        value_eur_per_unit: num(r.unitCost),
      })),
      unitValues: {
        building_eur_per_m2: num(unitValues.building),
        co2_eur_per_t: num(unitValues.co2),
        forest_eur_per_ha: {
          chene: num(unitValues.chene),
          pin: num(unitValues.pin),
          conifere: num(unitValues.conifere),
          feuillus: num(unitValues.feuillus),
          mixte: num(unitValues.mixte),
          shrubland: num(unitValues.shrubland),
          grassland: num(unitValues.grassland),
        },
      },
    };
    console.log("Cost payload:", payload);
    onLaunchCost(payload);
  };

  return (
    <div className="cost-step">
      <div className="cost-step__body">
        <header className="cost-step__header">
          <div>
            <h2>Coût des sauvés</h2>
            <p>Renseignez les valeurs unitaires et les moyens engagés.</p>
          </div>
          <button
            type="button"
            className="cost-step__toggle"
            onClick={() => setInputsOpen((o) => !o)}
            aria-expanded={inputsOpen}
            aria-controls="cost-inputs"
          >
            {inputsOpen ? "Replier" : "Modifier"}
            {inputsOpen ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </button>
        </header>

        {/* compact recap, only visible when folded */}
        {!inputsOpen && (
          <button
            type="button"
            className="cost-step__summary"
            onClick={() => setInputsOpen(true)}
          >
            <span>
              <b>{filledUnits}</b> /{" "}
              {UNIT_ROWS.filter((r) => !r.derived).length} valeurs unitaires
            </span>
            <span>
              <b>{namedResources.length}</b> moyen
              {namedResources.length > 1 ? "s" : ""} engagé
              {namedResources.length > 1 ? "s" : ""}
            </span>
            <span>
              Total moyens : <b>{resourcesTotal.toLocaleString("fr-FR")} €</b>
            </span>
          </button>
        )}

        <div
          id="cost-inputs"
          className={`cost-step__collapse ${inputsOpen ? "" : "is-collapsed"}`}
          {...(!inputsOpen ? { inert: "" } : {})}
        >
          <div className="cost-step__collapse-inner" ref={inputsRef}>
            <div className="cost-step__columns">
              <section className="cost-step__card">
                <h3>Valeurs unitaires</h3>
                {UNIT_ROWS.map(({ key, label, suffix, derived }) => (
                  <div className="cost-step__row" key={key}>
                    <span>{label}</span>
                    <span className="cost-step__field">
                      <input
                        type="number"
                        min="0"
                        aria-label={label}
                        disabled={derived}
                        title={
                          derived
                            ? "Toujours égal à 8 % de la valeur des bâtiments immobiliers"
                            : undefined
                        }
                        value={derived ? movableValue : (unitValues[key] ?? "")}
                        onChange={(e) => handleUnitChange(key, e.target.value)}
                      />
                      <small>{suffix}</small>
                    </span>
                  </div>
                ))}
              </section>

              <section className="cost-step__card cost-step__card--resources">
                <h3>Moyens engagés</h3>
                <div className="cost-step__resources-scroll">
                  {RESOURCE_CATEGORIES.map((cat) => {
                    const rows = resources.filter((r) => r.category === cat.id);
                    return (
                      <div className="cost-step__group" key={cat.id}>
                        <h4 className="cost-step__group-title">{cat.title}</h4>

                        <div className="cost-step__resources-list">
                          {rows.map((resource) => (
                            <div
                              className="cost-step__resource"
                              key={resource.id}
                            >
                              {cat.labelOptions ? (
                                <select
                                  className={`cost-step__resource-label cost-step__select ${
                                    resource.label === ""
                                      ? "is-placeholder"
                                      : ""
                                  } ${invalidClass(resource.id, "label")}`}
                                  aria-label={cat.labelPlaceholder}
                                  aria-invalid={
                                    isMissing(resource.id, "label") || undefined
                                  }
                                  value={resource.label}
                                  onChange={(e) =>
                                    handleResourceChange(
                                      resource.id,
                                      "label",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="" disabled>
                                    {cat.labelPlaceholder}
                                  </option>
                                  {cat.labelOptions.map((opt) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  className={`cost-step__resource-label ${invalidClass(resource.id, "label")}`}
                                  aria-label={cat.labelPlaceholder}
                                  aria-invalid={
                                    isMissing(resource.id, "label") || undefined
                                  }
                                  placeholder={cat.labelPlaceholder}
                                  value={resource.label}
                                  onChange={(e) =>
                                    handleResourceChange(
                                      resource.id,
                                      "label",
                                      e.target.value,
                                    )
                                  }
                                />
                              )}
                              <input
                                className={`cost-step__resource-num ${invalidClass(resource.id, "quantity")}`}
                                type="number"
                                min="0"
                                aria-label={cat.qtyPlaceholder}
                                aria-invalid={
                                  isMissing(resource.id, "quantity") ||
                                  undefined
                                }
                                placeholder={cat.qtyPlaceholder}
                                value={resource.quantity}
                                onChange={(e) =>
                                  handleResourceChange(
                                    resource.id,
                                    "quantity",
                                    e.target.value,
                                  )
                                }
                              />
                              <span aria-hidden="true">×</span>
                              <input
                                className={`cost-step__resource-num ${invalidClass(resource.id, "unitCost")}`}
                                type="number"
                                min="0"
                                aria-label={cat.pricePlaceholder}
                                aria-invalid={
                                  isMissing(resource.id, "unitCost") ||
                                  undefined
                                }
                                placeholder={cat.pricePlaceholder}
                                value={resource.unitCost}
                                onChange={(e) =>
                                  handleResourceChange(
                                    resource.id,
                                    "unitCost",
                                    e.target.value,
                                  )
                                }
                              />
                              <button
                                type="button"
                                className="cost-step__remove"
                                aria-label="Supprimer cette ligne"
                                onClick={() =>
                                  handleRemoveResource(resource.id)
                                }
                              >
                                <DeleteOutlinedIcon fontSize="small" />
                              </button>
                            </div>
                          ))}
                        </div>

                        <button
                          type="button"
                          className="cost-step__btn cost-step__btn--outline cost-step__add"
                          onClick={() => handleAddResource(cat.id)}
                        >
                          <AddIcon fontSize="small" /> {cat.addLabel}
                        </button>
                      </div>
                    );
                  })}{" "}
                </div>
              </section>
            </div>
            {incompleteCount > 0 && (
              <div className="cost-step__alert" role="alert">
                <ErrorOutlineIcon className="cost-step__alert-icon" />
                <p>
                  {incompleteCount > 1
                    ? `${incompleteCount} lignes incomplètes`
                    : "1 ligne incomplète"}{" "}
                  dans « Moyens engagés ». Renseignez le nom, la quantité et le
                  coût unitaire, ou videz / supprimez la ligne.
                </p>
              </div>
            )}
            <button
              type="button"
              className="cost-step__btn cost-step__btn--calculate"
              onClick={handleCalculate}
              disabled={cost?.isLoading}
            >
              {cost?.isLoading
                ? "Calcul en cours…"
                : cost?.data
                  ? "Recalculer"
                  : "Calculer"}
            </button>
          </div>
        </div>

        {cost?.isLoading && (
          <p className="cost-step__status" role="status">
            Calcul en cours…
          </p>
        )}

        {cost?.error && (
          <div className="cost-step__alert" role="alert">
            <ErrorOutlineIcon className="cost-step__alert-icon" />
            <p>{cost.error}</p>
          </div>
        )}

        {cost?.data?.money && <CostResults money={cost.data.money} />}
      </div>
      {cost?.data && (
        <ReportTypePicker value={reportType} onChange={setReportType} />
      )}
      <footer className="cost-step__footer">
        <button
          type="button"
          className="cost-step__btn cost-step__btn--ghost"
          onClick={onBack}
        >
          Retour
        </button>
        <button
          type="button"
          className="cost-step__btn cost-step__btn--next"
          onClick={onNext}
          disabled={!canContinue}
        >
          Générer le rapport
        </button>
      </footer>
    </div>
  );
};

export default CostStep;
